/**
 * useTranslation.ts
 *
 * React hook for instantaneous, reactive translation and formatting.
 */

import { useState, useEffect, useCallback } from 'react';
import { globalLocaleRegistry, LocaleRegistry } from './locale-registry.js';
import { globalTranslationService, TranslationService } from './translation-service.js';
import {
  formatDate as fmtDate,
  formatTime as fmtTime,
  formatDateTime as fmtDateTime,
  formatCurrency as fmtCurrency,
  formatNumber as fmtNumber,
  formatRelativeTime as fmtRelTime,
} from './formatters.js';
import type { Bcp47LocaleTag, TextDirection, TranslationParams } from './locale-contracts.js';

export function useTranslation(
  registry: LocaleRegistry = globalLocaleRegistry,
  translationService: TranslationService = globalTranslationService
) {
  const [locale, setLocaleState] = useState<Bcp47LocaleTag>(registry.getLocale());
  const [direction, setDirectionState] = useState<TextDirection>(registry.getDirection());

  useEffect(() => {
    return registry.subscribe((newLocale, newDir) => {
      setLocaleState(newLocale);
      setDirectionState(newDir);
    });
  }, [registry]);

  const t = useCallback(
    (key: string, params?: TranslationParams): string => {
      return translationService.translate(key, params, locale);
    },
    [translationService, locale]
  );

  const setLocale = useCallback(
    (newLocale: string) => {
      registry.setLocale(newLocale);
    },
    [registry]
  );

  const formatDate = useCallback(
    (timestamp: number | string | Date, timeZone?: string, options?: Intl.DateTimeFormatOptions) => {
      return fmtDate(timestamp, locale, timeZone, options);
    },
    [locale]
  );

  const formatTime = useCallback(
    (timestamp: number | string | Date, timeZone?: string, options?: Intl.DateTimeFormatOptions) => {
      return fmtTime(timestamp, locale, timeZone, options);
    },
    [locale]
  );

  const formatCurrency = useCallback(
    (amount: number, currency = 'USD') => {
      return fmtCurrency(amount, currency, locale);
    },
    [locale]
  );

  const formatNumber = useCallback(
    (val: number, options?: Intl.NumberFormatOptions) => {
      return fmtNumber(val, locale, options);
    },
    [locale]
  );

  const formatRelativeTime = useCallback(
    (diff: number, unit: Intl.RelativeTimeFormatUnit) => {
      return fmtRelTime(diff, unit, locale);
    },
    [locale]
  );

  return {
    t,
    locale,
    direction,
    isRtl: direction === 'rtl',
    setLocale,
    formatDate,
    formatTime,
    formatCurrency,
    formatNumber,
    formatRelativeTime,
  };
}
